/**
 * Zones Page - Microsoft 365 Style
 * Manage geographical zones for security operations
 */

import React, { useEffect, useState } from 'react';
import {
  makeStyles,
  Button,
  Input,
  Label,
  Text,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Textarea,
  Badge,
} from '@fluentui/react-components';
import { EditRegular, DeleteRegular, LocationRegular, SearchRegular } from '@fluentui/react-icons';
import { ContentCard, TableCommandBar } from '../../../components/layout';
import { SearchableDropdown, TablePagination, SlidePanel } from '../../../components/common';
import { usePagination } from '../../../hooks/usePagination';
import { ApiClient, newToast } from '../../../configurations/index.config';
import { getErrorMessage } from '../../../types';
import { ghanaRegions } from '../../../configurations/staticData';

const useStyles = makeStyles({
  pageHeader: {
    marginBottom: '24px',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
    display: 'block',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
    display: 'block',
  },
  searchBar: {
    marginBottom: '16px',
    maxWidth: '400px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #e1dfdd',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#323130',
  },
  tr: {
    borderBottom: '1px solid #e1dfdd',
    ':hover': {
      backgroundColor: '#f9f9f9',
    },
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#323130',
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginBottom: '16px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#605e5c',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
  },
});

interface Zone {
  id: string;
  name: string;
  coverage_description?: string;
  region?: string;
}

export const ZonesFluentUI: React.FC = () => {
  const styles = useStyles();
  const [zones, setZones] = useState<Zone[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedZone, setSelectedZone] = useState<Zone | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    region: '',
  });

  useEffect(() => {
    fetchZones();
  }, []);

  const fetchZones = async () => {
    setLoading(true);
    try {
      const response = await ApiClient.get('/get-zones');
      if (response.data.status === 'success') {
        setZones(response.data.data || []);
      } else {
        newToast('error', response.data?.message || 'Failed to fetch zones');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  const handleOpenDialog = (zone?: Zone) => {
    if (zone) {
      setSelectedZone(zone);
      setFormData({
        name: zone.name,
        description: zone.coverage_description || '',
        region: zone.region || '',
      });
    } else {
      setSelectedZone(null);
      setFormData({ name: '', description: '', region: '' });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.region) {
      newToast('warning', 'Please enter zone name and select a region');
      return;
    }

    try {
      const payload = {
        id: selectedZone?.id || '',
        name: formData.name,
        coverage_description: formData.description,
        region: formData.region,
      };

      const response = await ApiClient.post('/add-update-zone', payload);
      if (response.data.status === 'success') {
        newToast('success', response.data.message || 'Zone saved successfully');
        fetchZones();
        setDialogOpen(false);
      } else {
        newToast('error', response.data?.message || 'Failed to save zone');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
  };

  const handleDelete = async () => {
    if (!selectedZone) {
      return;
    }

    try {
      const response = await ApiClient.post('/delete-zone', {
        id: selectedZone.id,
      });

      if (response.data.status === 'success') {
        newToast('success', 'Zone deleted successfully');
        fetchZones();
      } else {
        newToast('error', response.data?.message || 'Failed to delete zone');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setDeleteDialogOpen(false);
    setSelectedZone(null);
  };

  const filteredZones = zones.filter(zone => {
    const query = searchQuery.toLowerCase();
    return (
      zone.name?.toLowerCase().includes(query) ||
      zone.coverage_description?.toLowerCase().includes(query) ||
      zone.region?.toLowerCase().includes(query)
    );
  });

  // Pagination
  const { currentPage, itemsPerPage, paginatedData, totalItems, setCurrentPage, setItemsPerPage } =
    usePagination({
      data: filteredZones,
      initialPage: 1,
      initialItemsPerPage: 10,
    });

  // Get region label
  const getRegionLabel = (regionValue: string) => {
    const region = ghanaRegions.find(r => r.value === regionValue);
    return region ? region.label : regionValue;
  };

  return (
    <div>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <Text className={styles.pageTitle}>Manage Zones</Text>
        <Text className={styles.pageSubtitle}>
          {loading ? 'Loading...' : `${zones.length} zones`}
        </Text>
      </div>

      {/* Command Bar */}
      <TableCommandBar onAdd={() => handleOpenDialog()} addLabel="Add Zone" />

      {/* Search Bar */}
      <div className={styles.searchBar}>
        <Input
          placeholder="Search zones..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          contentBefore={<SearchRegular />}
          size="medium"
        />
      </div>

      {/* Zones Table */}
      <ContentCard noPadding>
        {loading ? (
          <div className={styles.emptyState}>
            <Text>Loading zones...</Text>
          </div>
        ) : filteredZones.length === 0 ? (
          <div className={styles.emptyState}>
            <LocationRegular style={{ fontSize: '48px', marginBottom: '12px' }} />
            <Text size={500} weight="semibold" style={{ display: 'block' }}>
              No zones found
            </Text>
            <Text style={{ marginTop: '8px', display: 'block' }}>
              {searchQuery
                ? 'Try adjusting your search'
                : 'Click "Add Zone" to create your first zone'}
            </Text>
          </div>
        ) : (
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th className={styles.th}>Zone Name</th>
                <th className={styles.th}>Region</th>
                <th className={styles.th}>Coverage Description</th>
                <th className={styles.th} style={{ width: '150px' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map(zone => (
                <tr key={zone.id} className={styles.tr}>
                  <td className={styles.td}>
                    <Badge appearance="filled" color="brand">
                      {zone.name}
                    </Badge>
                  </td>
                  <td className={styles.td}>
                    {zone.region ? (
                      <Badge appearance="tint" color="informative">
                        {getRegionLabel(zone.region)}
                      </Badge>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className={styles.td}>{zone.coverage_description || '—'}</td>
                  <td className={styles.td}>
                    <div className={styles.actionButtons}>
                      <Button
                        size="small"
                        appearance="subtle"
                        icon={<EditRegular />}
                        onClick={() => handleOpenDialog(zone)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        appearance="subtle"
                        icon={<DeleteRegular />}
                        onClick={() => {
                          setSelectedZone(zone);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && filteredZones.length > 0 && (
          <TablePagination
            totalItems={totalItems}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        )}
      </ContentCard>

      {/* Add/Edit Slide Panel */}
      <SlidePanel
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title={selectedZone ? 'Edit Zone' : 'Add Zone'}
        subtitle={selectedZone ? 'Update zone details' : 'Create a new zone'}
        width="33%"
        footer={
          <>
            <Button appearance="secondary" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button appearance="primary" onClick={handleSave}>
              {selectedZone ? 'Update' : 'Create'}
            </Button>
          </>
        }
      >
        <div className={styles.formRow}>
          <div className={styles.formField}>
            <Label required>Zone Name</Label>
            <Input
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter zone name"
            />
          </div>

          <div className={styles.formField}>
            <Label required>Region</Label>
            <SearchableDropdown
              placeholder="Search region..."
              options={ghanaRegions.map(region => ({
                id: region.value,
                label: region.label,
              }))}
              selectedValue={formData.region}
              onSelect={value => setFormData({ ...formData, region: value })}
            />
          </div>
        </div>

        <div className={styles.formField}>
          <Label>Coverage Description</Label>
          <Textarea
            value={formData.description}
            onChange={e => setFormData({ ...formData, description: e.target.value })}
            placeholder="Enter coverage area description (optional)"
            rows={3}
          />
        </div>
      </SlidePanel>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(e, data) => setDeleteDialogOpen(data.open)}>
        <DialogSurface
          style={{
            maxWidth: '400px',
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <DialogBody>
            <DialogTitle>Delete Zone</DialogTitle>
            <DialogContent>
              Are you sure you want to delete <strong>{selectedZone?.name}</strong>? This action
              cannot be undone.
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={handleDelete}>
                Delete
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default ZonesFluentUI;
